'use client';

import {
    faArrowAltCircleDown,
    faArrowAltCircleUp,
    faCalendarAlt,
    faCheckSquare,
    faCreditCard,
    faEuroSign,
    faGripVertical,
    faHashtag,
    faLayerGroup,
    faList,
    faPen,
    faPlus,
    faTable,
    faTrash,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { cx } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { v4 as uuid } from 'uuid';
import { FormField, FormSectionChild } from './FormAssistent.types';
import { cloneField } from './helpers/fieldIdentity';

// Avoid circular dep: QuestionBlock imports SectionConfig, so we load it lazily
const QuestionBlock = dynamic(() => import('./QuestionBlock'));

const DRAG_TYPE = 'QUESTION';

// ── Local type maps (duplicated from QuestionBlock to avoid circular import) ───

const TYPE_LABELS: Record<string, string> = {
    text: 'Textfeld',
    address: 'Adresse',
    number: 'Zahlenfeld',
    wysiwyg: 'Textblock',
    checkbox: 'Kästchen',
    select: 'Auswahlfeld',
    date: 'Datum',
    file: 'Datei-Upload',
    iban: 'IBAN',
    table: 'Tabelle',
    payment_options: 'Zahlungsoptionen',
    pricing_table: 'Preistabelle',
    legal: 'Rechtliches',
    section: 'Abschnitt',
    signature: 'Unterschrift'
};

const TYPE_ICONS: Record<string, any> = {
    text: faPen,
    address: faPen,
    number: faHashtag,
    date: faCalendarAlt,
    file: faUpload,
    select: faList,
    checkbox: faCheckSquare,
    iban: faCreditCard,
    table: faTable,
    wysiwyg: faPen,
    section: faLayerGroup,
    payment_options: faEuroSign,
    pricing_table: faEuroSign
};

// ── Operator tables ────────────────────────────────────────────────────────────

const NUMBER_OPERATORS = [
    { value: '==', label: 'gleich' },
    { value: '!=', label: 'ungleich' },
    { value: '<', label: 'kleiner als' },
    { value: '<=', label: 'höchstens' },
    { value: '>', label: 'größer als' },
    { value: '>=', label: 'mindestens' }
] as const;

const DATE_OPERATORS = [
    { value: '==', label: 'genau am' },
    { value: '<', label: 'vor dem' },
    { value: '<=', label: 'bis einschließlich' },
    { value: '>', label: 'nach dem' },
    { value: '>=', label: 'ab einschließlich' }
] as const;

// ── ConditionSentence ──────────────────────────────────────────────────────────

interface ConditionSentenceProps {
    child: FormSectionChild;
    controllingField: FormField;
    onUpdate: (patch: Partial<FormSectionChild>) => void;
}

const ConditionSentence: React.FC<ConditionSentenceProps> = ({ child, controllingField, onUpdate }) => {
    const fieldLabel = controllingField.label || controllingField.name;
    const type = controllingField.type;

    const rawOptions =
        ['select', 'checkbox'].includes(type) && Array.isArray(controllingField.options)
            ? (controllingField.options as string[]).filter((o) => typeof o === 'string' && o.trim())
            : [];

    const isBooleanCheckbox = type === 'checkbox' && rawOptions.length === 0;
    const effectiveOptions = isBooleanCheckbox
        ? [
              { value: 'true', label: 'Angekreuzt' },
              { value: 'false', label: 'Nicht angekreuzt' }
          ]
        : rawOptions.map((o) => ({ value: o, label: o }));

    const linkedValues = child.linkedValues ?? [];

    const toggleChip = (val: string) => {
        const next = linkedValues.includes(val) ? linkedValues.filter((v) => v !== val) : [...linkedValues, val];
        onUpdate({ linkedValues: next });
    };

    const sentenceBase = (
        <span className="tw-text-xs tw-font-medium tw-text-success">
            Zeige diese Felder wenn <span className="tw-font-semibold">{fieldLabel}</span>
        </span>
    );

    if (type === 'number') {
        return (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-success/30 tw-bg-success/10 tw-px-3 tw-py-2">
                {sentenceBase}
                <select
                    className="tw-rounded-md tw-border tw-border-solid tw-border-success/40 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-success focus:tw-outline-none"
                    value={child.condition?.operator ?? '=='}
                    onChange={(e) =>
                        onUpdate({ condition: { operator: e.target.value as any, value: child.condition?.value ?? 0 } })
                    }
                >
                    {NUMBER_OPERATORS.map((op) => (
                        <option key={op.value} value={op.value}>
                            {op.label}
                        </option>
                    ))}
                </select>
                <input
                    type="number"
                    className="tw-w-20 tw-rounded-md tw-border tw-border-solid tw-border-success/40 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-success focus:tw-outline-none"
                    value={child.condition?.value ?? ''}
                    placeholder="Wert"
                    onChange={(e) =>
                        onUpdate({
                            condition: { operator: child.condition?.operator ?? '==', value: Number(e.target.value) }
                        })
                    }
                />
            </div>
        );
    }

    if (type === 'date') {
        return (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-success/30 tw-bg-success/10 tw-px-3 tw-py-2">
                {sentenceBase}
                <select
                    className="tw-rounded-md tw-border tw-border-solid tw-border-success/40 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-success focus:tw-outline-none"
                    value={child.condition?.operator ?? '=='}
                    onChange={(e) =>
                        onUpdate({
                            condition: { operator: e.target.value as any, value: child.condition?.value ?? '' }
                        })
                    }
                >
                    {DATE_OPERATORS.map((op) => (
                        <option key={op.value} value={op.value}>
                            {op.label}
                        </option>
                    ))}
                </select>
                <input
                    type="date"
                    className="tw-rounded-md tw-border tw-border-solid tw-border-success/40 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-success focus:tw-outline-none"
                    value={String(child.condition?.value ?? '')}
                    onChange={(e) =>
                        onUpdate({ condition: { operator: child.condition?.operator ?? '==', value: e.target.value } })
                    }
                />
            </div>
        );
    }

    if (type === 'text') {
        return (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-success/30 tw-bg-success/10 tw-px-3 tw-py-2">
                {sentenceBase}
                <span className="tw-text-xs tw-font-medium tw-text-success">gleich</span>
                <input
                    type="text"
                    className="tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-solid tw-border-success/40 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-success focus:tw-outline-none"
                    value={child.linkedValues?.[0] ?? ''}
                    placeholder="Erwarteter Textwert"
                    onChange={(e) => onUpdate({ linkedValues: [e.target.value] })}
                />
            </div>
        );
    }

    // select / checkbox — chip selector
    return (
        <div className="tw-rounded-lg tw-border tw-border-solid tw-border-success/30 tw-bg-success/10 tw-px-3 tw-py-2">
            <div className="tw-mb-2">
                {sentenceBase} <span className="tw-text-xs tw-font-medium tw-text-success">gleich</span>
            </div>
            {effectiveOptions.length === 0 ? (
                <p className="tw-text-[10px] tw-text-accents-5">Das gewählte Feld hat noch keine Optionen.</p>
            ) : (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    {effectiveOptions.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleChip(opt.value)}
                            className={cx(
                                'tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-0.5 tw-text-xs tw-transition-colors',
                                linkedValues.includes(opt.value)
                                    ? 'tw-border-success tw-bg-success tw-text-white'
                                    : 'tw-border-accents-3 tw-bg-white tw-text-accents-7 hover:tw-border-accents-4'
                            )}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── ControllingFieldPicker ─────────────────────────────────────────────────────

interface ControllingFieldPickerProps {
    field: FormField;
    availableControllingFields: FormField[];
    sectionChildren: FormSectionChild[];
    onReplaceField: (updated: FormField) => void;
}

const ControllingFieldPicker: React.FC<ControllingFieldPickerProps> = ({
    field,
    availableControllingFields,
    sectionChildren,
    onReplaceField
}) => {
    if (availableControllingFields.length === 0) {
        return (
            <p className="tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-1 tw-px-3 tw-py-2 tw-text-xs tw-text-accents-6">
                Füge oberhalb dieses Abschnitts ein Feld hinzu (Kästchen, Auswahl, Text, Datum oder Zahl) — es steuert
                dann, was angezeigt wird.
            </p>
        );
    }

    return (
        <div>
            <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">
                Welche Frage entscheidet, was angezeigt wird?
            </label>
            <select
                className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                value={field.dependsOnField ?? ''}
                onChange={(e) => {
                    const picked = availableControllingFields.find((c) => c.name === e.target.value);
                    onReplaceField({
                        ...field,
                        dependsOnField: e.target.value,
                        controllingType: (picked?.type ?? 'text') as FormField['controllingType'],
                        controllingOptions: [],
                        children: sectionChildren.map((c) => ({ ...c, linkedValues: [], condition: undefined }))
                    });
                }}
            >
                <option value="">— Bitte wählen —</option>
                {availableControllingFields.map((c) => (
                    <option key={c.name} value={c.name}>
                        {c.label || c.name}
                    </option>
                ))}
            </select>
        </div>
    );
};

// ── NestedFieldRowCompact ──────────────────────────────────────────────────────

interface DragItem {
    index: number;
    scope: string;
}

interface NestedFieldRowCompactProps {
    field: FormField;
    index: number;
    dragScope: string;
    labelError?: boolean;
    onActivate: () => void;
    onDelete: () => void;
    onMoveItem: (from: number, to: number) => void;
    onMoveEnd: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    canMoveUp: boolean;
    canMoveDown: boolean;
}

const NestedFieldRowCompact: React.FC<NestedFieldRowCompactProps> = ({
    field,
    index,
    dragScope,
    labelError = false,
    onActivate,
    onDelete,
    onMoveItem,
    onMoveEnd,
    onMoveUp,
    onMoveDown,
    canMoveUp,
    canMoveDown
}) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: DRAG_TYPE,
        item: { index, scope: dragScope },
        end: () => onMoveEnd(),
        collect: (monitor) => ({ isDragging: monitor.isDragging() })
    });

    const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
        accept: DRAG_TYPE,
        collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
        hover(item, monitor) {
            if (item.scope !== dragScope || item.index === index) return;
            const rect = rowRef.current?.getBoundingClientRect();
            if (!rect) return;
            const offset = monitor.getClientOffset();
            if (!offset) return;
            const mid = (rect.bottom - rect.top) / 2;
            const clientY = offset.y - rect.top;
            if (item.index < index && clientY < mid) return;
            if (item.index > index && clientY > mid) return;
            onMoveItem(item.index, index);
            item.index = index;
        }
    });

    drag(handleRef);
    drop(dragPreview(rowRef));

    return (
        <div
            ref={rowRef}
            className={[
                'tw-group tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-bg-white tw-px-3 tw-py-2 tw-transition-colors',
                isOver
                    ? 'tw-border-success tw-ring-2 tw-ring-success/20'
                    : labelError
                      ? 'tw-border-error tw-ring-2 tw-ring-error/10'
                      : 'tw-border-accents-3 hover:tw-border-accents-4 hover:tw-bg-accents-0',
                isDragging ? 'tw-opacity-40' : 'tw-opacity-100'
            ].join(' ')}
            onClick={(e) => {
                e.stopPropagation();
                onActivate();
            }}
        >
            <div
                ref={handleRef}
                className="tw-flex tw-cursor-grab tw-items-center tw-self-stretch tw-pr-1 tw-text-accents-4 active:tw-cursor-grabbing"
                onClick={(e) => e.stopPropagation()}
                title="Verschieben"
            >
                <FontAwesomeIcon icon={faGripVertical} className="tw-text-xs" />
            </div>
            <div className="tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-accents-2 tw-text-accents-7">
                <FontAwesomeIcon icon={TYPE_ICONS[field.type] ?? faPen} className="tw-text-xs" />
            </div>
            <span className="tw-flex-1 tw-truncate tw-text-sm tw-text-accents-9">
                {field.type === 'wysiwyg' ? 'Textblock' : field.label || 'Feld ohne Bezeichnung'}
            </span>
            {labelError && (
                <span className="tw-shrink-0 tw-rounded-full tw-bg-error/10 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-medium tw-text-error">
                    Titel fehlt
                </span>
            )}
            {field.rules?.includes('required') && (
                <span className="tw-shrink-0 tw-rounded-full tw-bg-error/10 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-medium tw-text-error">
                    Pflicht
                </span>
            )}
            <span className="tw-shrink-0 tw-rounded-full tw-bg-accents-2 tw-px-2 tw-py-0.5 tw-text-[10px] tw-text-accents-7">
                {TYPE_LABELS[field.type] ?? field.type}
            </span>
            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()}>
                <Button
                    type="button"
                    disabled={!canMoveUp}
                    color="secondary"
                    flat
                    onClick={onMoveUp}
                    className="tw-size-6 tw-max-w-4 tw-items-center tw-justify-center disabled:tw-cursor-not-allowed disabled:tw-opacity-30"
                >
                    <FontAwesomeIcon icon={faArrowAltCircleUp} className="tw-text-xs" />
                </Button>
                <Button
                    type="button"
                    disabled={!canMoveDown}
                    onClick={onMoveDown}
                    color="secondary"
                    flat
                    className="tw-size-6 tw-max-w-4 tw-items-center tw-justify-center disabled:tw-cursor-not-allowed disabled:tw-opacity-30"
                >
                    <FontAwesomeIcon icon={faArrowAltCircleDown} className="tw-text-xs" />
                </Button>
                <Button
                    color="error"
                    flat
                    type="button"
                    onClick={onDelete}
                    className="tw-size-6 tw-max-w-4 tw-items-center tw-justify-center disabled:tw-cursor-not-allowed disabled:tw-opacity-30"
                >
                    <FontAwesomeIcon icon={faTrash} className="tw-text-xs" />
                </Button>
            </div>
        </div>
    );
};

// ── SectionConfig ──────────────────────────────────────────────────────────────

export interface SectionConfigProps {
    field: FormField;
    allFields: FormField[];
    index: number;
    onFieldUpdate: (key: keyof FormField, value: any) => void;
    onReplaceField: (updated: FormField) => void;
    onOpenModal: (opts: { title: string; content: React.ReactNode | ((id: string) => React.ReactNode) }) => void;
    onCloseModal: (id: string) => void;
    labelErrorFieldNames?: string[];
}

const SectionConfig: React.FC<SectionConfigProps> = ({
    field,
    allFields,
    index,
    onFieldUpdate,
    onReplaceField,
    onOpenModal,
    onCloseModal,
    labelErrorFieldNames = []
}) => {
    const [activeNestedFieldId, setActiveNestedFieldId] = useState<string | null>(null);

    const isConditional = field.sectionMode === 'conditional';
    const sectionChildren: FormSectionChild[] = field.children ?? [];

    const availableControllingFields = allFields
        .slice(0, index)
        .filter((c) => ['checkbox', 'select', 'text', 'date', 'number'].includes(c.type));

    const selectedControllingField = availableControllingFields.find((c) => c.name === field.dependsOnField);

    // ── Child helpers ────────────────────────────────────────────────────────

    const updateSectionChildren = (children: FormSectionChild[]) => onReplaceField({ ...field, children });

    const updateSectionChild = (childId: string, patch: Partial<FormSectionChild>) =>
        updateSectionChildren(sectionChildren.map((c) => (c.id === childId ? { ...c, ...patch } : c)));

    const removeSectionChild = (childId: string) => {
        updateSectionChildren(sectionChildren.filter((c) => c.id !== childId));
        setActiveNestedFieldId(null);
    };

    const addSectionChild = () =>
        updateSectionChildren([
            ...sectionChildren,
            { id: uuid(), label: '', helperText: '', linkedValues: [], fields: [] }
        ]);

    const updateChildFields = (childId: string, updater: (fields: FormField[]) => FormField[]) =>
        updateSectionChildren(
            sectionChildren.map((child) =>
                child.id === childId ? { ...child, fields: updater(child.fields ?? []) } : child
            )
        );

    const moveChildField = (childId: string, from: number, to: number) => {
        updateChildFields(childId, (fields) => {
            if (from === to || from < 0 || from >= fields.length || to < 0 || to >= fields.length) return fields;
            const next = [...fields];
            const [moved] = next.splice(from, 1);
            if (!moved) return fields;
            next.splice(to, 0, moved);
            return next;
        });
    };

    const createNestedField = (): FormField => ({
        name: uuid(),
        type: 'text',
        label: 'Neues Feld',
        rules: [],
        placeholder: ''
    });

    const switchMode = (next: FormField['sectionMode']) => {
        onReplaceField({
            ...field,
            sectionMode: next,
            dependsOnField: next !== 'conditional' ? '' : field.dependsOnField,
            children: sectionChildren.map((c) => ({ ...c, linkedValues: [], condition: undefined }))
        });
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="tw-space-y-4">
            {/* Description */}
            <div>
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Beschreibung (optional)</label>
                <input
                    type="text"
                    className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                    value={field.helperText ?? ''}
                    placeholder="Kurze Einleitung für diesen Abschnitt"
                    onChange={(e) => onFieldUpdate('helperText', e.target.value)}
                />
            </div>

            {/* Mode toggle */}
            <div>
                <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium">
                    Wie soll dieser Abschnitt funktionieren?
                </label>
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                    {(
                        [
                            {
                                mode: 'default' as const,
                                title: 'Mehrere Schritte',
                                hint: 'Jeder durchläuft alle Schritte der Reihe nach'
                            },
                            {
                                mode: 'conditional' as const,
                                title: 'Je nach Antwort',
                                hint: 'Verschiedene Felder für verschiedene Fälle'
                            }
                        ] as const
                    ).map(({ mode, title, hint }) => {
                        const active = (field.sectionMode ?? 'default') === mode;
                        return (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => switchMode(mode)}
                                className={cx(
                                    'tw-flex tw-flex-col tw-items-center tw-rounded-xl tw-border tw-border-solid tw-p-2 tw-text-center tw-transition-colors',
                                    active
                                        ? 'tw-border-success/40 tw-bg-success/10 tw-text-success'
                                        : 'tw-border-accents-3 tw-bg-accents-0 tw-text-accents-7 hover:tw-border-accents-4 hover:tw-bg-accents-1'
                                )}
                            >
                                <span className="tw-text-sm tw-font-semibold">{title}</span>
                                <span
                                    className={cx(
                                        'tw-mt-0.5 tw-text-[12px]',
                                        active ? 'tw-text-success/70' : 'tw-text-accents-6'
                                    )}
                                >
                                    {hint}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Context hint */}
            <p className="tw-card-bordered tw-py-2 tw-text-sm tw-text-accents-7">
                {isConditional
                    ? '💡 Wähle eine Frage aus deinem Formular — abhängig von der Antwort werden verschiedene Felder angezeigt.'
                    : '💡 Die Felder werden in mehrere Schritte aufgeteilt — jeder Nutzer sieht alle Schritte der Reihe nach.'}
            </p>

            {/* Controlling field picker — conditional only */}
            {isConditional && (
                <ControllingFieldPicker
                    field={field}
                    availableControllingFields={availableControllingFields}
                    sectionChildren={sectionChildren}
                    onReplaceField={onReplaceField}
                />
            )}

            {/* Section children */}
            <div className="tw-space-y-3 tw-pb-5">
                {sectionChildren.map((child, i) => (
                    <div
                        key={child.id}
                        className="tw-space-y-3 tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-p-4"
                    >
                        {/* Header: number badge + name input + delete */}
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <span
                                className={cx(
                                    'tw-flex tw-size-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] tw-font-bold',
                                    isConditional ? 'tw-bg-success tw-text-white' : 'tw-bg-accents-5 tw-text-white'
                                )}
                            >
                                {i + 1}
                            </span>
                            <input
                                type="text"
                                className="tw-flex-1 tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-sm tw-font-medium tw-text-accents-9 focus:tw-border-success focus:tw-outline-none"
                                value={child.label}
                                placeholder={isConditional ? 'z. B. Unter 18 Jahre' : 'z. B. Schritt 1'}
                                onChange={(e) => updateSectionChild(child.id, { label: e.target.value })}
                            />
                            <Button
                                type="button"
                                color="error"
                                size="sm"
                                className="tw-shrink-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeSectionChild(child.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </div>

                        {/* Step description — multi-step only */}
                        {!isConditional && (
                            <textarea
                                rows={3}
                                className="tw-w-full tw-resize-y tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-accents-7 focus:tw-border-success focus:tw-outline-none"
                                value={child.helperText ?? ''}
                                placeholder="Beschreibung dieses Schritts (optional)"
                                onChange={(e) => updateSectionChild(child.id, { helperText: e.target.value })}
                            />
                        )}

                        {/* Condition sentence — conditional only, when a controlling field is selected */}
                        {isConditional && selectedControllingField && field.dependsOnField && (
                            <ConditionSentence
                                child={child}
                                controllingField={selectedControllingField}
                                onUpdate={(patch) => updateSectionChild(child.id, patch)}
                            />
                        )}

                        {/* Nested fields */}
                        <div
                            className="tw-space-y-1.5"
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveNestedFieldId(null);
                            }}
                        >
                            {(child.fields ?? []).map((childField, nestedIndex) =>
                                activeNestedFieldId === childField.name ? (
                                    <div key={childField.name} onClick={(e) => e.stopPropagation()}>
                                        <QuestionBlock
                                            field={childField}
                                            allFields={child.fields ?? []}
                                            index={nestedIndex}
                                            isActive={true}
                                            isRestricted={false}
                                            labelError={labelErrorFieldNames.includes(childField.name)}
                                            labelErrorFieldNames={labelErrorFieldNames}
                                            onActivate={() => setActiveNestedFieldId(childField.name)}
                                            onLabelChange={(label) =>
                                                updateChildFields(child.id, (fields) =>
                                                    fields.map((f) =>
                                                        f.name === childField.name ? { ...f, label } : f
                                                    )
                                                )
                                            }
                                            onTypeChange={(type) =>
                                                updateChildFields(child.id, (fields) =>
                                                    fields.map((f) => {
                                                        if (f.name !== childField.name) return f;
                                                        const base: FormField = {
                                                            name: f.name,
                                                            label: f.type === 'wysiwyg' ? '' : f.label,
                                                            rules: (f.rules ?? []).filter((r) => r === 'required'),
                                                            type
                                                        };
                                                        if (type === 'table')
                                                            return {
                                                                ...base,
                                                                rows: [],
                                                                columns: [],
                                                                disabledTimes: [],
                                                                minTimes: 1,
                                                                minDays: 1
                                                            };
                                                        if (type === 'select') return { ...base, options: ['', ''] };
                                                        if (type === 'payment_options') return { ...base, options: [] };
                                                        if (type === 'text' || type === 'iban')
                                                            return { ...base, placeholder: '' };
                                                        if (type === 'checkbox')
                                                            return { ...base, selectionMode: 'multiple' };
                                                        if (type === 'section')
                                                            return {
                                                                ...base,
                                                                helperText: '',
                                                                sectionMode: 'default',
                                                                children: [
                                                                    {
                                                                        id: uuid(),
                                                                        label: 'Schritt 1',
                                                                        helperText: '',
                                                                        fields: []
                                                                    }
                                                                ]
                                                            };
                                                        return base;
                                                    })
                                                )
                                            }
                                            onFieldUpdate={(key, value) =>
                                                updateChildFields(child.id, (fields) =>
                                                    fields.map((f) =>
                                                        f.name === childField.name ? { ...f, [key]: value } : f
                                                    )
                                                )
                                            }
                                            onReplaceField={(updated) =>
                                                updateChildFields(child.id, (fields) =>
                                                    fields.map((f) => (f.name === updated.name ? updated : f))
                                                )
                                            }
                                            onDuplicate={() =>
                                                updateChildFields(child.id, (fields) => {
                                                    const idx = fields.findIndex((f) => f.name === childField.name);
                                                    const next = [...fields];
                                                    // Deep copy — a shallow one would share nested names/ids
                                                    next.splice(idx + 1, 0, cloneField(childField));
                                                    return next;
                                                })
                                            }
                                            onInsertAbove={() =>
                                                updateChildFields(child.id, (fields) => {
                                                    const idx = fields.findIndex((f) => f.name === childField.name);
                                                    const next = [...fields];
                                                    next.splice(idx, 0, createNestedField());
                                                    return next;
                                                })
                                            }
                                            onInsertBelow={() =>
                                                updateChildFields(child.id, (fields) => {
                                                    const idx = fields.findIndex((f) => f.name === childField.name);
                                                    const next = [...fields];
                                                    next.splice(idx + 1, 0, createNestedField());
                                                    return next;
                                                })
                                            }
                                            onDelete={() =>
                                                updateChildFields(child.id, (fields) =>
                                                    fields.filter((f) => f.name !== childField.name)
                                                )
                                            }
                                            onMoveItem={(from, to) => moveChildField(child.id, from, to)}
                                            onMoveEnd={() => {}}
                                            onOpenModal={onOpenModal}
                                            onCloseModal={onCloseModal}
                                            dragScope={`section-child:${child.id}`}
                                        />
                                    </div>
                                ) : (
                                    <NestedFieldRowCompact
                                        key={childField.name}
                                        field={childField}
                                        index={nestedIndex}
                                        dragScope={`section-child:${child.id}`}
                                        labelError={labelErrorFieldNames.includes(childField.name)}
                                        onActivate={() => setActiveNestedFieldId(childField.name)}
                                        onDelete={() =>
                                            updateChildFields(child.id, (fields) =>
                                                fields.filter((f) => f.name !== childField.name)
                                            )
                                        }
                                        onMoveItem={(from, to) => moveChildField(child.id, from, to)}
                                        onMoveEnd={() => {}}
                                        onMoveUp={() => moveChildField(child.id, nestedIndex, nestedIndex - 1)}
                                        onMoveDown={() => moveChildField(child.id, nestedIndex, nestedIndex + 1)}
                                        canMoveUp={nestedIndex > 0}
                                        canMoveDown={nestedIndex < (child.fields?.length ?? 0) - 1}
                                    />
                                )
                            )}

                            {(child.fields ?? []).length === 0 && (
                                <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-accents-3 tw-px-4 tw-py-5 tw-text-center">
                                    <p className="tw-mb-0 tw-text-sm tw-text-accents-7">
                                        Noch keine Felder in {isConditional ? 'dieser Variante' : `Schritt ${i + 1}`}.
                                    </p>
                                    <p className="tw-mb-0 tw-text-xs tw-text-accents-6">
                                        Klicke auf &quot;Feld hinzufügen&quot; um loszulegen.
                                    </p>
                                </div>
                            )}

                            <Button
                                type="button"
                                color="light"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const nextField = createNestedField();
                                    updateChildFields(child.id, (fields) => [...fields, nextField]);
                                    setActiveNestedFieldId(nextField.name);
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} className="tw-mr-2 tw-text-xs" />
                                Feld in diesem Abschnitt hinzufügen
                            </Button>
                        </div>
                    </div>
                ))}

                <button
                    type="button"
                    onClick={addSectionChild}
                    className="tw-w-full tw-rounded-xl tw-border tw-border-dashed tw-border-accents-3 tw-bg-white tw-py-2 tw-text-xs tw-text-accents-5 tw-transition-colors hover:tw-border-success hover:tw-bg-success/5 hover:tw-text-success"
                >
                    <FontAwesomeIcon icon={faPlus} className="tw-mr-1.5" />
                    {isConditional ? 'Weitere Variante hinzufügen' : 'Schritt hinzufügen'}
                </button>
            </div>
        </div>
    );
};

export default SectionConfig;
